import type KnxProductData from "../../KnxProductdata";
import type Device from "../../models/Device";
import type { ChannelDescriptionProperty, ChannelTypeProperty } from "../../models/Channel";
import type { KnxSceneNumberProperty } from "../../models/ButtonChannel";
import type RawSceneDataItem from "../../models/RawSceneDataItem";
import type Signal from "../../models/Signal";
import Logger from "../../Logger";
import KnxInstanceData, { ValueLevels } from "../../models/KnxInstanceData";
import { matchN } from "../../KnxProductDataTools/determineActorComObjectFunction";
import { hasExcludedSceneName } from "../../KnxProductDataTools/hasExcludedSceneName";
import { isParameter, isParameterType, isTypeRestriction } from "../../Types";
import { showDebugErrToast } from "../Toasts";
import Allocator from "./Allocator";
import { firstChildWithTag } from "../XmlUtils";
import { MapValueToBuffer } from "../../models/programming/MapValueToBuffer";
import Channel from "../../models/Channel";

const logger = new Logger();

const wordMappings: { [key: string]: string[] } = {
  switch: ["schalten", "schalter"], // no itx
  value: ["wert"], // no itx
  temperature: ["temperatur"], // no itx
  humidity: ["feuchtigkeit"],
};

export default class KnxContext {
  private allocators: Allocator[] = [];

  public currentChannelDescriptionProperty: ChannelDescriptionProperty | undefined;
  public currentRawSceneData: RawSceneDataItem[] | undefined;
  public lastSignalNumber = 0;
  public currentKnxButtonSceneNumberProperty: KnxSceneNumberProperty | undefined;
  public lastSceneSignal: Signal | undefined;
  public currentChannelTypeProperty: ChannelTypeProperty | undefined;
  public xSignals: Signal[] = [];
  public rawSceneItemIndex = 0;
  public currentTable: any | undefined;
  public flatItems: any[] = [];
  public isChannelIndependentBlock = false;

  constructor(
    public readonly knxProductData: KnxProductData,
    public readonly knxInstanceData: KnxInstanceData,
    public readonly device: Device,
    public readonly isActor: boolean,
    public readonly isLandscape: boolean,
  ) {}

  getDataItem(id: string): any | undefined {
    return this.knxProductData?.getItem(id);
  }

  deref(item: any): any {
    return this.knxProductData?.deref(item);
  }

  getText(data: any, en: boolean = false): string {
    return this.knxProductData?.getTranslatedProperty("Text", data, en) || "";
  }

  getTextWithTranslation(data: any): string {
    if (
      data["name"] == "ParameterBlock" // no itx
    ) {
      var text = this.getText(data);

      const text1 = this.knxProductData.languageMapping.getTranslation(data.a.RefId, "Text");

      if (text == "#" || text == "") {
        text = this.getText(data);
      } else {
        if (text1) {
          text = text1;
        }
      }
      return text;
    } else if (data["#name"] == "ParameterBlock") {
      var text = this.getText(data);
      const text2 = this.knxProductData.languageMapping.getTranslation(this.deref(data?.a.ParamRefId)?.a.RefId, "Text");
      text = text2 || text;
      return text;
    } else {
      return data.a.Text || "";
    }
  }

  getName(data: any): string {
    let id = data?.a?.Id;

    do {
      const name = data?.a?.Name;
      if (name) {
        return name;
      }

      const refId = data?.a?.RefId;
      if (!refId) {
        break;
      }

      data = this.getDataItem(refId);
      id = data?.a?.Id;
    } while (id);

    return "";
  }

  setValueNoRerender(valueLevel: ValueLevels, newValue: any, id: string, instanceId: string, moduleId: string): void {
    this.knxInstanceData.setValue(valueLevel, newValue, id, instanceId, moduleId);
  }

  getExtendedData(id: string, instanceId: string): any {
    return this.knxInstanceData.getExtendedData(id, instanceId);
  }

  setAllocators(allocators: Allocator[]): void {
    this.allocators = allocators;
  }

  getValue(id: string, instanceId: string, ignoreHidden?: boolean, returnUnreferencedParam?: boolean): any {
    if (ignoreHidden) {
      const x = this.knxInstanceData.getExtendedData(id, instanceId);
      if (x?.access == "None") {
        return undefined;
      }
    }

    const v = this.knxInstanceData.getValue(id, instanceId, returnUnreferencedParam);
    if (v) {
      return v;
    }

    const ext = this.knxInstanceData.getExtendedData(id, instanceId);
    if (ext?.init?.InitialValue != undefined) {
      return ext.init.InitialValue;
    }

    return undefined;
  }

  getArgValueByName(args: any, name: string): string | undefined {
    for (let i in args) {
      const arg = args[i];
      if (arg.a.Name == name) {
        const AllocatorRefId = arg.a.AllocatorRefId;
        if (AllocatorRefId) {
          const allocator = this.allocators.find((it) => it.Id == AllocatorRefId);
          const fullIndex = allocator?.getCurrentIndex();

          if (fullIndex != undefined) {
            return `${parseInt(allocator?.Start || "0") + fullIndex}`;
          }

          logger.error("no repeatindex for allocator B");
          return "0";
        }

        return arg.a.Value;
      }
    }

    return undefined;
  }

  getAllocatorByParameterRefId(args: any | undefined, parameterRefId: string): Allocator | undefined {
    const arg = args?.find((candidate: any) => candidate.a.RefId == parameterRefId);
    if (arg) {
      const allocatorRefId = arg.a.AllocatorRefId;
      if (allocatorRefId) {
        return this.allocators.find((allocator) => allocator.Id == allocatorRefId);
      }
    }

    return undefined;
  }

  getArgSizeByRefId(args: any | undefined, id: string): string | undefined {
    if (args) {
      for (let i in args) {
        const arg = args[i];
        if (arg.a.RefId == id) {
          return arg.a?.Allocates;
        }
      }
    }

    return undefined;
  }

  getArgAllocatesByRefId(args: any | undefined, id: string): string | undefined {
    return this.getArgSizeByRefId(args, id);
  }

  getArgByRefId(args: any | undefined, id: string): any | undefined {
    if (args) {
      for (let i in args) {
        const arg = args[i];
        if (arg.a.RefId == id) {
          return arg;
        }
      }
    }

    return undefined;
  }

  getArgValueByRefId(args: any | undefined, id: string): string | undefined {
    const arg = this.getArgByRefId(args, id);
    return arg?.a.Value;
  }

  setValueAssign(newValue: any, id: string, instanceId: string, moduleId: string): void {
    this.knxInstanceData.setValue(ValueLevels.assign, newValue, id, instanceId, moduleId, true);
  }

  setMemoryInfo(fullLIndexFromAllocator: number | undefined, instanceId: string, item: any, args: any): void {
    const parameterRef = this.knxProductData?.globalMap.get(item.a.RefId);
    const parameter = this.knxProductData?.globalMap.get(parameterRef.a.RefId);
    const refId = item.a.RefId;

    let memory;

    if (parameter.union) {
      memory = firstChildWithTag(parameter.union, "Memory"); // no itx
    } else {
      memory = firstChildWithTag(parameter, "Memory"); // no itx
    }

    if (memory) {
      const parameterTypeId = item.a.ParameterType;
      const parameterType = this.knxProductData?.globalMap.get(parameterTypeId);

      const baseOffsetArgName = memory.a.BaseOffset;
      const baseOffset = this.getArgValueByRefId(args, baseOffsetArgName);
      const argSize = parseInt(this.getArgSizeByRefId(args, baseOffsetArgName) || "0");
      const codeSegmentId = memory.a.CodeSegment;

      let type = "none";
      let sizeInBit = "0";
      if (parameterType) {
        const parameterRestriction = parameterType.TR || parameterType.TypeRestriction;
        if (parameterRestriction) {
          type = "unsignedInt"; // no itx
          sizeInBit = parameterRestriction?.a.SizeInBit;
        } else if (parameterType.TypeText) {
          type = "text";
          sizeInBit = parameterType.TypeText?.a.SizeInBit;
        } else if (parameterType.TypeNumber) {
          type = parameterType.TypeNumber?.a.Type || "number"; // could be signedInt or unsignedInt no itx
          sizeInBit = parameterType.TypeNumber.a.SizeInBit;
        } else if (parameterType.TypeFloat) {
          type = "float";
          sizeInBit = "0";
        }
      }

      const offsetMemory = memory.a.Offset;
      const bitOffsetMemory = memory.a.BitOffset;

      const parameterBitOffset = parameter.a.BitOffset;
      const parameterOffset = parameter.a.Offset;
      const access = parameter.a?.Access;

      let fullOffset = 0;
      const allocator = this.getAllocatorByParameterRefId(args, baseOffsetArgName);
      if (allocator && fullLIndexFromAllocator != undefined) {
        const maxInclusive = parseInt(allocator.maxInclusive || "0");
        fullOffset = fullLIndexFromAllocator * argSize; // (parseInt(sizeInBit) / 8);
        fullOffset += parseInt(offsetMemory || "0");
        fullOffset += parseInt(parameterOffset || "0");
      } else {
        fullOffset = parseInt(baseOffset || "0");
        fullOffset += parseInt(offsetMemory || "0");
        fullOffset += parseInt(parameterOffset || "0");
      }

      let fullBitOffset = parseInt(bitOffsetMemory || "0");
      fullBitOffset += parseInt(parameterBitOffset || "0");

      let baseOffsetValue = parseInt(baseOffset || "0");

      const unionSize = parseInt(parameter.union?.a.SizeInBit || "8");

      const codeSegment = this.device.programming.getCodeSegment(codeSegmentId);
      codeSegment.mappings.push(
        new MapValueToBuffer({
          fullOffset: fullOffset,
          fullBitOffset: fullBitOffset,
          sizeInBit: parseInt(sizeInBit),
          type: type,
          union: parameter.union,
          baseOffset: baseOffsetValue,
          parameterType: parameterType,
          access: access,
          propertyId: refId,
          instanceId: instanceId,
          unionSize: unionSize,
          defaultValue: parameter.a.Value,
          name: parameter.a?.Name,
        }),
      );
    }
  }

  isSceneItem(item: any): boolean {
    if (!item) {
      logger.error("isSceneItem: item == undefined");
      return false;
    }

    const parameter = this.deref(item);
    const parameterType = this.deref(parameter?.a?.ParameterType);
    // Exclude addresses and counts before any numeric, name or text scene detection.
    if (hasExcludedSceneName(item?.a?.Name, parameter?.a?.Name, parameterType?.a?.Name)) {
      return false;
    }

    const name = this.getName(item).toLocaleLowerCase();

    let text = this.getText(item, true);
    let res = false;

    if (this.knxProductData?.isSceneNumberItem(item)) {
      res = true;
    }

    if (!res) {
      if (
        matchN(
          [
            "scene", // no itx
            "szene", // no itx
          ],
          name,
        )
      ) {
        res = true;
      }
    }

    if (!res) {
      if (text) {
        text = text.toLocaleLowerCase();
        if (
          matchN(
            [
              "scene", // no itx
              "szene", // no itx
            ],
            text,
          )
        ) {
          res = true;
        }
      }
    }

    if (isParameter(item)) {
      const parameterType = this.deref(item.a.ParameterType);
      if (!isParameterType(parameterType)) {
        showDebugErrToast("KnxParameterViewer - isSceneItem: parameterType does not conform to type"); // no itx
      } else {
        const innerItem = parameterType.items[0];
        if (isTypeRestriction(innerItem)) {
          const isSceneType = innerItem.items.some((enumeration) => matchN(["scene", "szene"], enumeration.a.Text)); // no itx
          if (isSceneType) {
            res = true;
          }
        }
      }
    } else {
      showDebugErrToast("KnxParameterViewer - isSceneItem: item is not a Parameter"); // no itx
    }

    if (res) {
      const textEN = this.knxProductData.getTranslatedProperty("Text", item, true);

      const positiveWords = [
        "8-bit", // Siemens DimActor 5WG1554-1DB31 // no itx
      ];
      if (matchN(positiveWords, name) || matchN(positiveWords, textEN)) {
        return true;
      }

      const ignoreWords = [
        "save", // no itx
        "speichern", // no itx
        "lear", // no itx
        "weitere", // no itx
        "more", // no itx
        "symbol", // no itx
        "bit", // no itx
        "download", // no itx
        "symbol", // no itx
        "overwrite", // no itx
      ];
      if (matchN(ignoreWords, name) || matchN(ignoreWords, textEN)) {
        return false;
      }
    }

    return res;
  }

  private matchCurlyBraket(input: string): { p1: number; p2: number; text: string } | undefined {
    const p1 = input.indexOf("{{");
    let p2 = input.indexOf("}}");

    let p1Nested = input.substring(p1 + 2, p2).indexOf("{{");
    if (p1Nested >= 0) {
      p2 = input.indexOf("}}", p2 + 2);
      p1Nested = input.substring(p1Nested + 2, p2).indexOf("{{");
    }

    if (p1 >= 0 && p2 >= 0) {
      return {
        p1: p1,
        p2: p2,
        text: input.substring(p1 + 2, p2),
      };
    }

    return undefined;
  }

  substitutePlaceholders(props: {
    input: string;
    args: any;
    repeatindex: number | undefined;
    params: any;
    title0?: string;
    instanceId: string;
    moduleId: string;
    channel?: Channel;
    signal?: Signal;
  }): string {
    var { input, args, title0, instanceId, signal, channel, moduleId } = props;

    if (input == undefined) {
      return "";
    }

    if (input.indexOf("{{") < 0) {
      return input;
    }

    title0 ||= "";

    if (title0.indexOf("{{") >= 0) {
      title0 = "";
    }

    var output = input + "";
    let matchInfo = this.matchCurlyBraket(output);
    while (matchInfo) {
      var defaultValue;
      let relation = matchInfo.text;

      var v = "";
      var id: string | undefined;

      const p = relation.indexOf(":");
      if (p > 0) {
        defaultValue = relation.substring(p + 1, relation.length).trim();

        if (defaultValue.indexOf("{{") >= 0) {
          defaultValue = this.substitutePlaceholders({ ...props, input: defaultValue });
        }

        relation = relation.substring(0, p).trim();
      }

      if (relation == "0") {
        if (!title0 || title0 == "-/-") {
          v = defaultValue || "";
        } else if (channel) {
          if (defaultValue) {
            const defaultValueIsChannelName = defaultValue && /[a-zA-Z0-9]/.test(defaultValue);
            if (defaultValue && defaultValueIsChannelName) {
              v = `(${defaultValue}) - ${title0}`;
            } else {
              v = title0;
            }
          } else {
            v = title0;
          }
        } else {
          v = title0;
        }
      } else if (parseInt(relation).toString() == relation) {
        id = this.knxInstanceData.getIdByRelation(relation, instanceId);
        if (id) {
          // this.knxInstanceData.addReEvalId(id, instanceId);
          this.knxInstanceData.setReferenced(id, instanceId);
          v = this.getValue(id, instanceId);
          if (signal) {
            signal.knxChannelDescriptionProperty = {
              id: id,
              instanceId: instanceId,
              moduleId: moduleId,
              xid: "",
            };
          }
        }
      } else {
        const s = this.getArgValueByName(args, matchInfo.text);
        if (s) {
          output = output.substring(0, matchInfo.p1) + s + output.substring(matchInfo.p2 + 2);
          matchInfo = undefined;
        } else {
          output = output.substring(0, matchInfo.p1) + (defaultValue || "") + output.substring(matchInfo.p2 + 2);
          matchInfo = undefined;
        }
      }

      if ((v == undefined || v == "") && defaultValue) {
        v = defaultValue;
        if (id) {
          this.setValueAssign(v, id, instanceId, moduleId);
        }
      }

      if (matchInfo) {
        output = output.substring(0, matchInfo.p1) + v + output.substring(matchInfo.p2 + 2);
      }

      matchInfo = this.matchCurlyBraket(output);
    }

    return output;
  }

  //
  // translates known words in string "s" to english
  //
  // MWE: 1.12.2025 todo: skip if product supports english language
  //
  translateToEnglish(s: string): string {
    for (const eng in wordMappings) {
      wordMappings[eng].forEach((translation) => {
        s = s.replaceAll(translation, eng);
      });
    }

    return s;
  }

  derefKnxDescriptionProperty(signal: Signal) {
    if (signal.knxChannelDescriptionProperty) {
      const knxDescriptionProperty = signal.knxChannelDescriptionProperty;
      let cnt = 0;
      let fromId;
      do {
        fromId = this.knxInstanceData.getAssignForToId(
          knxDescriptionProperty.id,
          knxDescriptionProperty.instanceId,
        )?.fromId;

        if (fromId) {
          knxDescriptionProperty.instanceId = "";
          knxDescriptionProperty.moduleId = "";
          knxDescriptionProperty.id = fromId;
        }
      } while (cnt++ < 3 && fromId);
    }
  }
}
